import { HttpClient, HttpParams } from '@angular/common/http';
import { EventEmitter, Injector } from '@angular/core';
import { BaseDto, GetManyResponse } from '@authority-labs/api-interfaces';
import { AuthenticationService } from '@check-list/app/auth/authentication.service';
import {
  SearchRequest,
  SearchRequestInt
} from '@check-list/app/classes/models';
import { ChangeType } from '@check-list/core/enums/change-type.enum';
import { CrudItemChanges } from '@check-list/core/models/crud-changes.model';
import { environment } from '@check-list/environments/environment';
import { RequestQueryBuilder } from '@nestjsx/crud-request';
import { ClassConstructor, plainToInstance } from 'class-transformer';
import { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';

export class CrudService<T extends BaseDto> {
  static changesEmitter = new EventEmitter<CrudItemChanges<BaseDto>>();

  public http: HttpClient;
  public api: string;
  authService: AuthenticationService;

  constructor(
    injector: Injector,
    private path: string,
    private classType: ClassConstructor<T>
  ) {
    this.http = injector.get(HttpClient);
    this.authService = injector.get(AuthenticationService);
    this.api = `${environment.API}/${path}`;
  }

  getMany(queryParams: string = ''): Observable<GetManyResponse<T>> {
    const options = { params: new HttpParams({ fromString: queryParams }) };
    return this.http.get<GetManyResponse<T>>(this.api, options).pipe(
      map((resp) => {
        resp.data = this.manyPlainToClass(resp.data);
        return resp;
      }),
      tap((resp) =>
        this._emit(ChangeType.receivedFromDB, resp.data, queryParams)
      )
    );
  }

  getAll(qb?: RequestQueryBuilder): Observable<T[]> {
    qb = qb ?? RequestQueryBuilder.create();
    qb = qb.setLimit(0);

    return this.getMany(qb.query()).pipe(map((resp) => resp.data));
  }

  getManyNoPagination(queryParams: string = ''): Observable<T[]> {
    const options = { params: new HttpParams({ fromString: queryParams }) };
    return this.http.get<T[]>(this.api, options).pipe(
      map((resp) => this.manyPlainToClass(resp)),
      tap((data) => {
        this._emit(ChangeType.receivedFromDB, data, queryParams);
      })
    );
  }

  getOne(id: number, queryParams: string = ''): Observable<T> {
    const options = { params: new HttpParams({ fromString: queryParams }) };
    return this.http.get<T>(`${this.api}/${id}`, options).pipe(
      map(this.plainToClass),
      tap((resp) => this._emit(ChangeType.receivedFromDB, [resp], queryParams))
    );
  }

  createOne(item: T, queryParams: string = ''): Observable<T> {
    const options = { params: new HttpParams({ fromString: queryParams }) };
    return this.http
      .post<T>(this.api, this.removeReadonlyFields({ ...item }), options)
      .pipe(
        map(this.plainToClass),
        tap((resp) => this._emit(ChangeType.created, [resp], queryParams))
      );
  }

  createMany(items: T[]): Observable<T[]> {
    return this.http
      .post<T[]>(
        this.api,
        items.map((item) => this.removeReadonlyFields({ ...item }))
      )
      .pipe(
        map(this.manyPlainToClass),
        tap((resp) => this._emit(ChangeType.created, resp))
      );
  }

  updateOne(id: number, item: T, queryParams: string = ''): Observable<T> {
    const options = { params: new HttpParams({ fromString: queryParams }) };
    return this.http
      .patch<T>(
        `${this.api}/${id}`,
        this.removeReadonlyFields({ ...item }),
        options
      )
      .pipe(
        map(this.plainToClass),
        tap((resp) => this._emit(ChangeType.edited, [resp], queryParams))
      );
  }

  replaceOne(id: number, item: T, queryParams: string = ''): Observable<T> {
    const options = { params: new HttpParams({ fromString: queryParams }) };

    item = this.removeReadonlyFields({ ...item });
    item = this.keepOnlyReferencedFieldsIds(item);

    return this.http.put<T>(`${this.api}/${id}`, item, options).pipe(
      map(this.plainToClass),
      tap((resp) => this._emit(ChangeType.edited, [resp], queryParams))
    );
  }

  deleteOne(id: number): Observable<void> {
    return this.http
      .delete<void>(`${this.api}/${id}`)
      .pipe(tap(() => this._emit(ChangeType.deleted, null)));
  }

  search(searchRequest: SearchRequestInt): Observable<GetManyResponse<T>> {
    const sr = new SearchRequest(searchRequest);
    const qb = RequestQueryBuilder.create();
    const searchByFields = sr.searchFields.map((field) => ({
      [field]: {
        [this._isNumberField(field) ? '$eq' : '$cont']: this._isNumberField(
          field
        )
          ? Number(sr.value)
          : sr.value
      }
    }));

    const filters = sr.filters.map((filter) => ({
      [filter.field]: { [filter.operator]: filter.value }
    }));

    let query = qb
      .search({
        $or: sr.value
          ? [...searchByFields, { $and: filters }]
          : [{ $and: filters }]
      })
      .setPage(sr.page || 1)
      .setLimit(sr.limit);

    if (sr.selectFields) {
      query = qb.select(sr.selectFields);
    }
    if (sr.joinFields) {
      query = qb.setJoin(sr.joinFields);
    }

    return this.getMany(query.query());
  }

  exportData(queryParams: string = ''): Observable<void> {
    return this.http
      .get(`${this.api}/export/data.xlsx`, {
        params: new HttpParams({ fromString: queryParams }),
        responseType: 'blob',
        observe: 'response'
      })
      .pipe(
        tap((response) => {
          const contentDisposition = response.headers.get(
            'Content-Disposition'
          );
          const filename =
            contentDisposition?.match(/filename\*=UTF-8''(.+?)$/i)?.[1] ??
            contentDisposition?.match(/filename="?([^";]+)"?/i)?.[1] ??
            'export.xlsx';

          const url = URL.createObjectURL(response.body);
          const a = document.createElement('a');
          a.href = url;
          a.download = decodeURIComponent(filename);
          a.click();
          URL.revokeObjectURL(url);
        }),
        map(() => void 0)
      );
  }

  removeReadonlyFields(item: T): T {
    delete item.createdAt;
    delete item.updatedAt;
    delete item.createdBy;
    delete item.updatedBy;
    delete item.deletedAt;

    return item;
  }

  manyPlainToClass(items: T[]): T[] {
    return plainToInstance(this.classType, items);
  }

  plainToClass = (item: T): T => plainToInstance(this.classType, item);

  protected keepOnlyReferencedFieldsIds = (item: T): T => item;

  private _isNumberField(field: string): boolean {
    // todo
    return field === 'id' || field === 'projectId' || field === 'subProjectId';
  }

  private _emit(action: ChangeType, items: T[], queryParams?: string) {
    CrudService.changesEmitter.emit({
      action,
      items,
      path: this.path,
      queryParams
    });
  }
}
